/**
 * ⟦C27 · A-153⟧ `Accueil` : les 4 répliques de l'accueil version 1 (« l'aveu des 48 h »,
 * `docs/REPLIQUES-MATT.md`), jouées avec la voix de Matt, PLEIN ÉCRAN, entre la fin de l'intro
 * (`screens/Intro/Intro.tsx`) et l'arrivée de l'interface F3 de la chambre. Montée par `App.tsx`,
 * au-dessus de `<Routeur/>`, sur l'état de `stores/accueilStore.ts`, même raison d'être là que
 * `<Intro/>` (voir le commentaire de `introStore.ts`) : la VRAIE chambre (`Studio.tsx`) est déjà
 * montée dessous, cet écran se contente de la couvrir puis de s'effacer.
 *
 * **Décision de Matt du 13/09/2026** (`docs/chantiers/C27-la-voix-de-matt.md`) : « garde tous les
 * échanges de Matt du début avec le full screen comme l'animation avant de mettre toute
 * l'interface, et il faut que l'interface vienne par dessus en glissant pour éviter le jumpcut ».
 *
 * **Deux temps** :
 * 1. **Les répliques** (`glissement === false`) : la planche d'arrivée `chambre-n1-jour-<genre>-
 *    debout.png`, cadrée comme la dernière image de l'intro (même `object-position`, même
 *    voile), la boîte de Matt en bas avec les 4 répliques de `REPLIQUES_BRIEFING` (déjà écrites
 *    pour `game/decouverte.ts` ⟦A-151⟧, jamais retapées ici en double), la voix les pilote
 *    (`BoiteDialogueAudio`, ⟦C27 · A-153⟧ §2). « Passer » (le ruban de la boîte), un tap long ou
 *    Échap sautent direct au glissement. Pendant ce temps, `Studio.tsx` ne rend RIEN, aucun
 *    en-tête, aucune fenêtre, aucun onglet dans le DOM (voir son commentaire).
 * 2. **Le glissement** (`glissement === true`, déclenché par `onGlisser`, la dernière réplique
 *    allée à son terme ou « Passer ») : la chambre plein écran se réduit en douceur (technique
 *    FLIP, mesurer le rectangle RÉEL de `.zone-scene`/`.scene-mobile-zone`, que `Studio.tsx`
 *    vient de monter au même instant, puis animer un `transform` depuis le plein écran vers lui,
 *    500 ms, ease-out) pendant que la boîte se retire vers le bas ; `Studio.tsx`, de son côté,
 *    anime l'arrivée de son en-tête et de ses fenêtres en cascade (`.interface-arrivee`,
 *    `app.css`). Une fois les deux animations terminées, `onTermine` retire cet écran pour de
 *    bon, la VRAIE chambre, déjà en place, prend le relais sans qu'il y ait rien à raccorder.
 *
 * `prefers-reduced-motion` : le glissement devient un fondu enchaîné de 200 ms, sans aucun
 * déplacement (§3 du chantier, repris à l'identique de `Studio.tsx` pour l'arrivée de
 * l'interface).
 */

import { useEffect, useRef, useState } from "react";

import type { GenreAvatar } from "@maxime/shared";

import { BoiteDialogue, type Interlocuteur } from "../../components/BoiteDialogue";
import { useSfx } from "../../components/SoundProvider";
import {
  echellePieceMobile,
  fondChambreN1,
  HAUTEUR_IMAGE_CHAMBRE,
  LARGEUR_IMAGE_CHAMBRE,
  rectangleImageAffichee,
  rectangleImagePixels,
  type AjustementScene,
} from "../../game/chambre";
import { REPLIQUES_BRIEFING } from "../../game/decouverte";
import { useUIStore } from "../../stores/uiStore";
import { REQUETE_FENETRES_MOBILE } from "../Chambre/Studio";

/** Même hôte que `NouvelleRun.tsx`/`Studio.tsx`/`Intro.tsx`, retapé à l'identique (convention
 *  déjà posée par ces trois écrans, jamais un import cross-écran pour une poignée de champs). */
const HOTE_MATT: Interlocuteur = { nom: "Matt", portrait: "/avatar/portrait-mathieu.png" };

/**
 * ⟦C42 · A-165 §1⟧ Un fichier PAR réplique, plus de découpe à la lecture (voir le commentaire de
 * tête de `components/BoiteDialogue.tsx`) : `accueil-1.m4a`..`accueil-4.m4a`, indexés comme
 * `REPLIQUES_BRIEFING` (même ordre, même longueur, produits depuis les 4 mêmes segments validés
 * par Matt — voir `client/public/son/matt/PROVENANCE.md`). Remplace `accueil.m4a` +
 * `accueil.json` (le fichier concaténé et ses repères) comme source de lecture ; les deux
 * restent sur le disque, jamais supprimés (le chantier le demande explicitement), simplement
 * plus branchés ici.
 */
const SOURCES_VOIX_ACCUEIL = [
  "/son/matt/accueil-1.m4a",
  "/son/matt/accueil-2.m4a",
  "/son/matt/accueil-3.m4a",
  "/son/matt/accueil-4.m4a",
] as const;

/** La scène montrée pendant l'accueil : toujours la toute première chambre, de jour (l'accueil
 *  ne joue qu'à la création d'une chaîne, avant même que l'horloge simulée n'ait tourné). Un
 *  seul nom, partagé par la planche affichée (`chambrePlanche`) et par le calcul du rectangle
 *  d'arrivée du glissement mobile (⟦C27 ter⟧, `rectangleImagePixels`) : les deux doivent
 *  toujours parler de la même scène, jamais deux constantes qui pourraient diverger. */
const SCENE_ACCUEIL = "chambre-n1-jour";

/** Un tap tenu au moins ce temps-là saute l'accueil, comme « Passer » ou Échap, liberté
 *  d'implémentation (le chantier demande le geste, pas un chiffre), même ordre de grandeur que
 *  les autres seuils de « tenue » du jeu (`DUREE_TENUE_TRAVAIL_MS`, `Studio.tsx`). */
const SEUIL_APPUI_LONG_MS = 500;

/** Le rectangle qui sert de cible au glissement de la chambre (mesuré en pixels écran, donc pas
 *  un `DOMRect` complet, seuls `left`/`top`/`width`/`height` servent ici). */
export interface RectangleCible {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * ⟦C27 · A-153⟧ La technique FLIP en une fonction pure et testable : le `transform` qui fait
 * passer un élément plein écran (`largeurFenetre` × `hauteurFenetre`) au rectangle `cible`,
 * mesuré dans le MÊME référentiel (coordonnées écran). `scale` non uniforme (X et Y séparés) :
 * l'image en `cover` à l'intérieur se déforme légèrement pendant la transition plutôt que de
 * recadrer différemment à chaque frame, un compromis assumé, invisible à l'œil sur 500 ms.
 */
export function transformVersRectangle(largeurFenetre: number, hauteurFenetre: number, cible: RectangleCible): string {
  const echelleX = cible.width / largeurFenetre;
  const echelleY = cible.height / hauteurFenetre;
  const translationX = cible.left + cible.width / 2 - largeurFenetre / 2;
  const translationY = cible.top + cible.height / 2 - hauteurFenetre / 2;
  return `translate(${translationX}px, ${translationY}px) scale(${echelleX}, ${echelleY})`;
}

/** Même ancrage que `object-position: 50% 58%` sur `.accueil-chambre-fondo` (`app.css`) —
 *  dupliqué ici en JS parce que `transformCadrageAccueil` doit reproduire CE cadrage au pixel
 *  près avant de le faire interpoler vers celui du jeu (⟦C27 quater⟧, défaut n°1 : voir le
 *  commentaire de tête). Changer l'un sans l'autre déferait la continuité visuelle que les deux
 *  garantissent ensemble au tout premier instant du glissement. */
const CADRAGE_ACCUEIL_X = 0.5;
const CADRAGE_ACCUEIL_Y = 0.58;

/**
 * ⟦C27 quater · défaut n°1⟧ **Le rectangle de l'image d'accueil, AVANT tout glissement**, dans
 * le même repère local que `transformVersRectangle` (celui de `.accueil-chambre-capa`, jamais
 * transformé par lui-même — un `transform` CSS ne change pas le calcul de mise en page dont
 * `object-fit`/`object-position` dépendent, voir plus bas pourquoi c'est précisément le bug).
 *
 * `.accueil-chambre-fondo` affichait jusqu'ici son cadrage via `object-fit: cover` +
 * `object-position: 50% 58%` en CSS pur, recalculé par le navigateur à chaque frame — mais
 * TOUJOURS par rapport à la boîte de LAYOUT de `.accueil-chambre-capa`, qui reste plein écran du
 * premier au dernier instant du glissement (un `transform` la scale visuellement, sans jamais
 * réduire sa taille de mise en page). Résultat : le cadrage `cover` de l'image ne changeait
 * JAMAIS pendant tout le rétrécissement — une bande verticale zoomée (le buste), immobile,
 * simplement affichée de plus en plus petite — puis la vraie chambre (mise en page bien plus
 * large que haute, cadrage totalement différent) apparaissait d'un coup au démontage. Constat du
 * chantier, `planche-reel-run1.png`.
 *
 * Le correctif : calculer ce même rectangle `cover` **en JS**, l'assigner à l'image en position
 * ABSOLUE (`transform-origin: 0 0`, taille de base `LARGEUR_IMAGE_CHAMBRE` ×
 * `HAUTEUR_IMAGE_CHAMBRE`), pour pouvoir ensuite le faire INTERPOLER via une transition CSS
 * ordinaire vers `transformCadrageJeu` (plus bas) — un `transform` sur l'image elle-même, cette
 * fois, PEUT être animé, puisqu'il ne dépend plus d'un recalcul de mise en page à chaque frame.
 * Au tout premier instant du glissement, cette valeur est PIXEL IDENTIQUE à ce que le CSS
 * affichait juste avant (même formule que `object-fit: cover` + `object-position`, `Math.max`
 * partagé avec `rectangleImageAffichee`) : le changement de technique ne produit, à lui seul,
 * aucun saut.
 */
export function transformCadrageAccueil(largeurFenetre: number, hauteurFenetre: number): string {
  const { largeur: largeurAffichee, hauteur: hauteurAffichee } = rectangleImageAffichee(
    { largeur: largeurFenetre, hauteur: hauteurFenetre },
    "cover",
  );
  const echelle = largeurAffichee / LARGEUR_IMAGE_CHAMBRE;
  const x = (largeurFenetre - largeurAffichee) * CADRAGE_ACCUEIL_X;
  const y = (hauteurFenetre - hauteurAffichee) * CADRAGE_ACCUEIL_Y;
  return `translate(${x}px, ${y}px) scale(${echelle})`;
}

/**
 * ⟦C27 quater · défaut n°1, corrigé une deuxième fois⟧ **Le rectangle VRAI (non rogné) de
 * l'image mise à l'échelle mobile**, dans le repère de la boîte qui la contient
 * (`.scene-mobile-zone`) — PAS le rectangle VISIBLE que renvoie `rectangleImagePixels`
 * (`game/chambre.ts`), qui clampe chaque bord à [0, 100] % de la boîte.
 *
 * **Trouvé en vérifiant, pas en lisant le code** (`diff.py` de ce chantier, une différence
 * d'images avant/après démontage qui ne descendait pas sous ~27 niveaux malgré un cadrage a
 * priori déjà corrigé) : une première version de ce correctif utilisait `rectangleImagePixels`
 * ici — correct pour poser un HOTSPOT (un point hors champ doit se ramener au bord), mais faux
 * pour CE calque, qui a besoin de la taille RÉELLE de l'image pour l'étirer sans la déformer,
 * puis laisser le débord se faire rogner par le `overflow: hidden` du calque — exactement ce que
 * fait `background-size`/`background-position` dans `Chambre.tsx`. `rectangleImagePixels`
 * rogne l'axe qui déborde (ici le plus souvent la largeur, l'échelle mobile étant choisie pour
 * dépasser légèrement, `echellePieceMobile`) mais PAS l'autre (l'ancrage haut ne rogne jamais la
 * hauteur) : son rectangle n'a donc PLUS le ratio 1024:576 dès qu'un seul bord est rogné, et
 * l'étirer en `object-fit: fill` écrasait l'image au lieu de la rogner — un « fantôme » décalé,
 * invisible sur une planche isolée, mais qui grandissait avec la distance au bord d'ancrage
 * (mesuré par `diff.py`, planche de chaleur à l'appui).
 *
 * `mode` : toujours `echellePieceMobile(scene)` ici (jamais `"cover"`/`"contain"` littéral),
 * ancrage "haut" implicite (seul cas pertinent au moment de l'accueil, cf. le commentaire de
 * `mesurer` dans le composant) : centrée horizontalement (`(boite.largeur - largeurAffichee) /
 * 2`, même formule que `positionDansBoite`), collée en haut verticalement (`top: 0`, aucun
 * rognage vertical par construction de cet ancrage).
 */
export function rectangleImageVraie(boite: { largeur: number; hauteur: number }, mode: AjustementScene): RectangleCible {
  const { largeur: largeurAffichee, hauteur: hauteurAffichee } = rectangleImageAffichee(boite, mode);
  return {
    left: (boite.largeur - largeurAffichee) / 2,
    top: 0,
    width: largeurAffichee,
    height: hauteurAffichee,
  };
}

/**
 * ⟦C27 quater · défaut n°1⟧ **Le rectangle de l'image, ANTICIPÉ dans le repère du calque avant
 * SON propre transform** — parce que le calque (`.accueil-chambre-capa`) a lui-même un transform
 * non trivial (`transformVersRectangle`, plein écran → `cible`), positionner l'image directement
 * à son rectangle FINAL voulu la ferait atterrir ailleurs une fois ce transform composé par
 *-dessus : il faut compenser la mise à l'échelle du calque par avance, pas viser directement.
 *
 * Le calque envoie tout son rectangle local `[0, 0, largeurFenetre, hauteurFenetre]` sur `cible`
 * (`transformVersRectangle`) : un point local `P` finit donc à `origine + échelle × (P −
 * origine) + translation`, avec `échelle = cible.taille / fenêtre`. Pour qu'un enfant positionné
 * en `L` (repère local, AVANT ce transform) atterrisse exactement sur `voulu = { left:
 * cible.left + image.left, top: cible.top + image.top, width: image.width, height: image.height
 * }` (le rectangle RÉEL de l'image, `rectangleImageVraie` ci-dessus, repositionné aux
 * coordonnées de `cible`), il faut donc INVERSER ce transform — et l'inverse se simplifie, la
 * position de `cible` s'annulant dans le calcul :
 *
 * ```
 * L.left   = largeurFenetre × image.left / boite.largeur
 * L.top    = hauteurFenetre × image.top  / boite.hauteur
 * L.width  = image.width  × largeurFenetre / boite.largeur
 * L.height = image.height × hauteurFenetre / boite.hauteur
 * ```
 *
 * (`boite`, ici, désigne la taille de `cible` elle-même — PAS forcément `.scene-mobile-zone` en
 * entier : `image`, l'appelant, doit être exprimé dans CE repère, celui que le calque cible
 * réellement — cf. le commentaire de `mesurer` dans le composant sur pourquoi `cible` reste un
 * sous-rectangle de la scène, jamais la boîte entière). Intuition : `L` est `image` (positionné
 * dans `boite`) agrandi comme si `boite` remplissait toute la fenêtre — exactement ce que le
 * calque fera ensuite en sens inverse. Cas particulier qui sert de garde-fou (`Accueil.test.ts`)
 * : si l'image remplit `boite` entièrement (`image = { left: 0, top: 0, width: boite.largeur,
 * height: boite.hauteur }`, le cas desktop, où `boite` EST la boîte entière puisque `cible = rect`
 * là-bas), `L` vaut exactement `[0, 0, largeurFenetre, hauteurFenetre]` — l'ancien calcul
 * (identité), le nouveau le généralise sans le casser.
 *
 * **Trouvé en vérifiant, pas en lisant le code** : une version antérieure de cette fonction
 * prenait `voulu` directement comme rectangle local, sans cette inversion — correct UNIQUEMENT
 * quand le calque ne bouge pas (identité), faux dès qu'il interpole réellement vers `cible` :
 * composer les deux transforms envoyait alors l'image ailleurs que `voulu`, un « fantôme »
 * décalé mesuré par `diff.py` (planche de chaleur à l'appui) avant d'être retrouvé au calcul.
 */
export function transformCadrageJeu(
  fenetre: { largeur: number; hauteur: number },
  boite: { largeur: number; hauteur: number },
  image: RectangleCible,
): string {
  const left = (fenetre.largeur * image.left) / boite.largeur;
  const top = (fenetre.hauteur * image.top) / boite.hauteur;
  const largeur = (image.width * fenetre.largeur) / boite.largeur;
  const hauteur = (image.height * fenetre.hauteur) / boite.hauteur;
  return `translate(${left}px, ${top}px) scale(${largeur / LARGEUR_IMAGE_CHAMBRE}, ${hauteur / HAUTEUR_IMAGE_CHAMBRE})`;
}

/** Durée du rétrécissement de la chambre (§3 du chantier, chiffré). */
export const DUREE_GLISSE_CHAMBRE_MS = 500;
/** Durée d'un élément d'interface qui arrive (`Studio.tsx`, `.interface-arrivee`). */
export const DUREE_ARRIVEE_ELEMENT_MS = 450;
/** Décalage entre deux éléments d'interface qui arrivent en cascade. */
export const DECALAGE_ARRIVEE_MS = 60;
/** En-tête, Ma chaîne, Journal, Actions, 4 éléments en cascade sur ordinateur. */
const NB_ELEMENTS_ARRIVEE = 4;
/** Le temps total de la cascade (le dernier élément commence à `(NB_ELEMENTS_ARRIVEE - 1) ×
 *  DECALAGE_ARRIVEE_MS` et dure encore `DUREE_ARRIVEE_ELEMENT_MS`). */
const DUREE_TOTALE_ARRIVEE_MS = DECALAGE_ARRIVEE_MS * (NB_ELEMENTS_ARRIVEE - 1) + DUREE_ARRIVEE_ELEMENT_MS;
/** La sortie de l'accueil ne se retire que lorsque LES DEUX animations concurrentes (le
 *  rétrécissement de la chambre ET la cascade de l'interface) sont terminées. */
export const DUREE_SORTIE_MS = Math.max(DUREE_GLISSE_CHAMBRE_MS, DUREE_TOTALE_ARRIVEE_MS);
/** `prefers-reduced-motion` : un simple fondu, chiffré par le chantier (§3). */
export const DUREE_SORTIE_REDUITE_MS = 200;

export interface AccueilProps {
  genre: GenreAvatar;
  /** `true` : le glissement est en cours (déclenché par `onGlisser`), l'accueil anime sa
   *  propre sortie et attend `DUREE_SORTIE_MS` (ou réduite) avant d'appeler `onTermine`. */
  glissement: boolean;
  /** La dernière réplique est allée à son terme, ou « Passer »/tap long/Échap : lance le
   *  glissement (`stores/accueilStore.ts`, marque au passage `briefing` vue). */
  onGlisser(): void;
  /** Le glissement est terminé : l'accueil se retire pour de bon. */
  onTermine(): void;
}

export function Accueil({ genre, glissement, onGlisser, onTermine }: AccueilProps) {
  const reducedMotion = useUIStore((s) => s.reducedMotion);
  const sfx = useSfx();

  // ⟦C27 · A-153⟧ Le son de glissement (`fenetre_glisse`, un des 5 proposés par C28, jamais
  // branché ailleurs) : une fois, au tout début du glissement, jamais sous mouvement réduit,
  // qui remplace le glissement par un simple fondu (un bruit de glisse sur un fondu immobile
  // serait incohérent, liberté d'implémentation cf. `PROVENANCE.md`).
  useEffect(() => {
    if (glissement && !reducedMotion) sfx("fenetre_glisse");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glissement, reducedMotion]);

  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    if (typeof matchMedia !== "function") return;
    const mq = matchMedia(REQUETE_FENETRES_MOBILE);
    const maj = () => setMobile(mq.matches);
    maj();
    mq.addEventListener("change", maj);
    window.addEventListener("resize", maj);
    return () => {
      mq.removeEventListener("change", maj);
      window.removeEventListener("resize", maj);
    };
  }, []);

  const [index, setIndex] = useState(0);
  // ⟦C42 · A-165 §1⟧ La durée de chacun des 4 fichiers, préchargée AVANT le montage de la boîte
  // (même raison d'être que l'ancien fetch de `accueil.json` qu'elle remplace) : connaître la
  // durée à l'avance cale le débit de la machine à écrire sur CHAQUE réplique dès son premier
  // caractère, sans attendre qu'un nouvel élément `<audio>` (un nouveau fichier à chaque
  // réplique, désormais) résolve ses propres métadonnées à la volée — voir le commentaire de
  // `BoiteDialogueAudio.duree`. `undefined` : le préchargement n'a pas encore répondu, la boîte
  // n'est pas montée tant que ça dure (4 fichiers en local, quelques dizaines de Ko, un
  // aller-retour négligeable), pour ne jamais démarrer une réplique sans le rythme de la voix
  // puis basculer dessous une fois les métadonnées arrivées. Une entrée `null` dans le tableau :
  // CE fichier a échoué (absent, réseau) — repli silencieux sur le rythme par défaut de
  // `BoiteDialogue` pour cette réplique-là seulement, jamais une erreur à l'écran.
  const [dureesVoix, setDureesVoix] = useState<(number | null)[] | undefined>(undefined);
  useEffect(() => {
    let vivant = true;
    Promise.all(
      SOURCES_VOIX_ACCUEIL.map(
        (src) =>
          new Promise<number | null>((resolve) => {
            const a = new Audio();
            a.preload = "metadata";
            a.addEventListener("loadedmetadata", () => resolve(Number.isFinite(a.duration) ? a.duration : null), {
              once: true,
            });
            a.addEventListener("error", () => resolve(null), { once: true });
            a.src = src;
          }),
      ),
    ).then((durees) => {
      if (vivant) setDureesVoix(durees);
    });
    return () => {
      vivant = false;
    };
  }, []);

  // ⟦C34 · A-159⟧ `fondChambreN1`, TOUJOURS liège vide + cadres photo d'origine — exactement
  // l'état que demande ce chantier (le mur ne se remplit qu'au déblocage), et de toute façon le
  // SEUL état possible ici : cet écran ne joue qu'à la création d'une run neuve
  // (`accueilStore.actif`, jamais rejoué ensuite, voir `Studio.tsx`), l'arbre et les tendances ne
  // sont par construction JAMAIS débloqués à cet instant. ⟦C33 · A-158⟧ Avant ce chantier, le
  // panneau rempli (le dessin d'avant C33) y aurait été un mensonge visuel de quelques secondes
  // avant que la vraie chambre (`Chambre.tsx`, qui lit `ouvert(state, "arbre")`) ne bascule sur
  // le liège vide juste après — remarque devenue sans objet, `fondChambreN1` ne connaît plus que
  // cet état.
  const chambrePlanche = fondChambreN1(genre);

  const racineRef = useRef<HTMLDivElement>(null);
  const capaRef = useRef<HTMLDivElement>(null);
  const [transformSortie, setTransformSortie] = useState<string | null>(null);
  // ⟦C27 quater · défaut n°1⟧ `null` tant que la mesure n'a pas eu lieu (même patron que
  // `transformSortie`, juste au-dessus) : le premier rendu où `glissement` passe à `true` garde
  // alors `transformCadrageAccueil` (calculé plus bas, sans dépendre du DOM) comme repli — pixel
  // identique à ce qu'affichait `object-fit: cover` l'instant d'avant, voir son commentaire.
  const [transformImageArrivee, setTransformImageArrivee] = useState<string | null>(null);

  const onGlisserRef = useRef(onGlisser);
  onGlisserRef.current = onGlisser;
  const onTermineRef = useRef(onTermine);
  onTermineRef.current = onTermine;

  function passer() {
    onGlisserRef.current();
  }

  function avancer() {
    if (index < REPLIQUES_BRIEFING.length - 1) {
      setIndex((i) => i + 1);
      return;
    }
    passer();
  }

  // « Passer » (dans le ruban de la boîte, voir le rendu plus bas), un tap long ou Échap
  // sautent l'accueil, à tout instant tant que le glissement n'a pas commencé (§1 du chantier).
  useEffect(() => {
    if (glissement) return;
    let debutAppui = 0;
    let skipEnCours = false;
    function onPointerDown() {
      debutAppui = performance.now();
    }
    function onPointerUp() {
      if (debutAppui && performance.now() - debutAppui >= SEUIL_APPUI_LONG_MS) {
        skipEnCours = true;
        passer();
      }
      debutAppui = 0;
    }
    // Un tap long qui vient de déclencher `passer()` ne doit pas EN PLUS avancer la boîte (le
    // `click` qui suit le relâchement, lui, ne regarde ni la durée ni la position du geste).
    function onClickCapture(e: MouseEvent) {
      if (skipEnCours) {
        skipEnCours = false;
        e.stopPropagation();
        e.preventDefault();
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") passer();
    }
    const racine = racineRef.current;
    racine?.addEventListener("pointerdown", onPointerDown);
    racine?.addEventListener("pointerup", onPointerUp);
    racine?.addEventListener("click", onClickCapture, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      racine?.removeEventListener("pointerdown", onPointerDown);
      racine?.removeEventListener("pointerup", onPointerUp);
      racine?.removeEventListener("click", onClickCapture, true);
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glissement]);

  // Le glissement : mesure le rectangle RÉEL de la scène de `Studio.tsx` (déjà montée, au même
  // rendu, voir le commentaire de tête) et programme le retrait final.
  useEffect(() => {
    if (!glissement) return;
    function mesurer() {
      const cibleEl = document.querySelector<HTMLElement>(mobile ? ".scene-mobile-zone" : ".zone-scene");
      if (!cibleEl) return;
      const rect = cibleEl.getBoundingClientRect();
      // ⟦C27 ter⟧ **Desktop inchangé** : `cover` remplit toujours 100 % de `.zone-scene`, le
      // rectangle de l'image affichée EST celui de la boîte, aucun changement de ce côté
      // (`transformVersRectangle` directement sur `rect`, comme depuis ⟦C27 bis⟧).
      //
      // **Mobile** : le calque vise le sous-rectangle VISIBLE de l'image (`rectangleImagePixels`,
      // rogné aux bords de la boîte), PAS la boîte `.scene-mobile-zone` entière. ⟦C27 quater⟧ a
      // d'abord essayé la boîte entière (« un conteneur de découpe qui interpole vers la boîte de
      // la scène », lu au pied de la lettre) : ça réintroduisait exactement le défaut n°3 —
      // `.scene-mobile-zone` s'étend jusqu'en bas de l'écran, LÀ où flotte la fenêtre Actions
      // (`.panneau-mobile`)/la boîte de Matt (`.matt-mobile`, `position: absolute`, PAR-DESSUS la
      // scène) ; un calque opaque aussi grand que la boîte les recouvre entièrement jusqu'au
      // démontage, alors que ⟦C27 bis⟧ les révèle déjà PROGRESSIVEMENT à travers le fond qui fond
      // de l'overlay. Revenu au sous-rectangle (le comportement de ⟦C27 ter⟧, qui laissait le
      // reste de la boîte au fond qui fond) : seul son CONTENU change (l'image, en dessous,
      // n'est plus rognée par `object-fit: cover` mais par le `overflow: hidden` du calque, voir
      // `rectangleImageVraie`/`transformCadrageJeu`).
      const cibleImage = mobile
        ? rectangleImagePixels({ largeur: rect.width, hauteur: rect.height }, echellePieceMobile(SCENE_ACCUEIL), "haut")
        : null;
      const cible = cibleImage
        ? { left: rect.left + cibleImage.x, top: rect.top + cibleImage.y, width: cibleImage.largeur, height: cibleImage.hauteur }
        : rect;
      setTransformSortie(transformVersRectangle(window.innerWidth, window.innerHeight, cible));
      // ⟦C27 quater · défaut n°1⟧ Mobile seulement : desktop garde `.accueil-chambre-fondo` en
      // `object-fit: cover` CSS ordinaire (l'image couvre déjà 100 % de la boîte, ⟦C27 ter⟧,
      // aucun changement de ce côté). `rectangleImageVraie` donne le rectangle RÉEL (non rogné,
      // contrairement à `cibleImage` ci-dessus) de l'image que `Chambre.tsx` dessine — mesuré sur
      // la MÊME largeur de boîte (`rectangleImageAffichee`, mode NOMBRE, ne dépend que de la
      // largeur, jamais de la hauteur : passer la boîte entière ou `cibleImage` donne le même
      // résultat ici) —, puis reprojeté dans le repère de `cibleImage` (soustraction de son coin
      // `x, y`, lui-même déjà rogné à 0 dans ce cas précis mais calculé en toute généralité) avant
      // `transformCadrageJeu`, qui anticipe la mise à l'échelle que le calque va appliquer
      // au-dessus (voir son commentaire pour la preuve).
      if (mobile && cibleImage) {
        const boite = { largeur: rect.width, hauteur: rect.height };
        const imageVraie = rectangleImageVraie(boite, echellePieceMobile(SCENE_ACCUEIL));
        const imageVraieDansCible = {
          left: imageVraie.left - cibleImage.x,
          top: imageVraie.top - cibleImage.y,
          width: imageVraie.width,
          height: imageVraie.height,
        };
        setTransformImageArrivee(
          transformCadrageJeu(
            { largeur: window.innerWidth, hauteur: window.innerHeight },
            { largeur: cibleImage.largeur, hauteur: cibleImage.hauteur },
            imageVraieDansCible,
          ),
        );
      }
    }
    mesurer();
    // Au cas où `Studio.tsx` peindrait sa scène une frame après ce commit (jamais vu en
    // pratique, les deux stores notifient dans le même cycle React, mais une chambre qui ne
    // bouge pas du tout vaut mieux qu'une chambre qui se fige au mauvais endroit).
    const frame = requestAnimationFrame(mesurer);

    // ⟦C27 quater · défaut n°2⟧ **Le démontage attendait un minuteur fixe, pas la vraie fin de
    // l'animation.** Un `window.setTimeout(DUREE_SORTIE_MS)` tourne en temps RÉEL (horloge
    // système), qu'importe la vitesse à laquelle le CSS anime réellement — sur un appareil qui
    // rame au démarrage (images qui se décodent, audio qui s'amorce) ou sous throttling, le
    // minuteur peut se déclencher AVANT que le calque n'ait fini de se rétrécir : saut garanti.
    // Constat du chantier, ralenti ×10 par CDP (qui ralentit les transitions CSS, jamais les
    // `setTimeout` JS, d'où le découplage) : le calque n'avait fait que 844 → 741 px quand
    // l'accueil se démontait déjà à 698 ms. Corrigé : attendre `transitionend` sur les DEUX
    // transitions qui gouvernent ce qu'on voit à l'écran — le calque lui-même (`capaRef`,
    // `transform` ou `opacity` en mouvement réduit) ET le fond de l'overlay (`racineRef`,
    // `background-color`, ⟦C27 bis⟧, qui dure plus longtemps que le calque en mouvement normal,
    // 630 contre 500 ms : le laisser filer est ce qui évite un résidu de teinte visible pile au
    // démontage, cf. le rapport du chantier, critère (c)). Sécurité à 2× la durée nominale si
    // jamais `transitionend` ne se déclenche pas (onglet en arrière-plan, transition interrompue
    // par un remontage) : mieux vaut un démontage tardif qu'un accueil qui reste bloqué à l'écran.
    const capaEl = capaRef.current;
    const racineEl = racineRef.current;
    let capaFini = false;
    let overlayFini = false;
    let dejaTermine = false;
    function terminerUneFois() {
      if (dejaTermine) return;
      dejaTermine = true;
      window.clearTimeout(secours);
      onTermineRef.current();
    }
    function surTransitionCapa(e: TransitionEvent) {
      if (e.target !== capaEl || (e.propertyName !== "transform" && e.propertyName !== "opacity")) return;
      capaFini = true;
      if (overlayFini) terminerUneFois();
    }
    function surTransitionOverlay(e: TransitionEvent) {
      if (e.target !== racineEl || e.propertyName !== "background-color") return;
      overlayFini = true;
      if (capaFini) terminerUneFois();
    }
    capaEl?.addEventListener("transitionend", surTransitionCapa);
    racineEl?.addEventListener("transitionend", surTransitionOverlay);
    const dureeNominale = reducedMotion ? DUREE_SORTIE_REDUITE_MS : DUREE_SORTIE_MS;
    const secours = window.setTimeout(terminerUneFois, dureeNominale * 2);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(secours);
      capaEl?.removeEventListener("transitionend", surTransitionCapa);
      racineEl?.removeEventListener("transitionend", surTransitionOverlay);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glissement, mobile, reducedMotion]);

  const styleSortieChambre = glissement
    ? reducedMotion
      ? { transition: `opacity ${DUREE_SORTIE_REDUITE_MS}ms ease-out`, opacity: 0 }
      : { transition: `transform ${DUREE_GLISSE_CHAMBRE_MS}ms ease-out`, transform: transformSortie ?? undefined }
    : undefined;
  const styleSortieBoite = glissement
    ? reducedMotion
      ? { transition: `opacity ${DUREE_SORTIE_REDUITE_MS}ms ease-out`, opacity: 0 }
      : {
          transition: `transform ${DUREE_ARRIVEE_ELEMENT_MS}ms ease-out, opacity ${DUREE_ARRIVEE_ELEMENT_MS}ms ease-out`,
          transform: "translateY(120%)",
          opacity: 0,
        }
    : undefined;
  // ⟦C27 bis⟧ **La cause du jump cut.** `.accueil-overlay` porte son propre `background:
  // var(--liseré)` (app.css), OPAQUE, à `z-index: 100` : il couvre TOUTE la fenêtre, y compris
  // la surface qui n'est plus recouverte par `.accueil-chambre-capa` une fois qu'elle a rétréci.
  // Studio.tsx, dessous, montait pourtant bien sa vraie interface en cascade (`.interface-
  // arrivee`) dès le premier instant du glissement : l'animation tournait réellement, mais
  // entièrement CACHÉE derrière ce fond opaque, invisible du premier au dernier pixel, jusqu'à
  // ce que `onTermine()` démonte l'accueil pour de bon (`DUREE_SORTIE_MS`) et révèle d'un coup
  // l'interface déjà arrivée à son état final : le jump cut n'est jamais dans les animations
  // elles-mêmes, seulement dans ce qui les cachait. Faire fondre ce fond vers transparent, sur
  // la MÊME durée que le reste du glissement, laisse la vraie interface se révéler
  // PROGRESSIVEMENT derrière la chambre qui rétrécit, et rend le démontage final imperceptible
  // (le fond est déjà transparent quand `<Accueil/>` disparaît du DOM).
  const styleSortieOverlay = glissement
    ? {
        transition: `background-color ${reducedMotion ? DUREE_SORTIE_REDUITE_MS : DUREE_SORTIE_MS}ms ease-out`,
        backgroundColor: "transparent",
      }
    : undefined;

  // ⟦C27 quater · défaut n°1 bis⟧ **Trouvé en vérifiant la pixel-perfection du critère (c), pas
  // en lisant le code.** `.accueil-chambre-voile` (le dégradé sombre posé sur l'image pour que le
  // texte de Matt reste lisible, §1 du chantier) n'a JAMAIS été fondu par aucun chantier
  // précédent : il reste à SON opacité pleine (jusqu'à 0,62 en bas de l'image) du premier au
  // dernier instant du glissement, y compris une fois `.accueil-chambre-capa` immobile à son
  // cadrage final — alors que la VRAIE scène, elle, n'a aucun voile. Le calque pouvait donc
  // devenir pixel identique en CADRAGE (défaut n°1 corrigé plus haut) tout en restant
  // SYSTÉMATIQUEMENT plus sombre que la vraie scène jusqu'au démontage, un écart mesuré (diff
  // avant/après démontage) qui grandit avec la profondeur du dégradé — exactement le profil
  // mesuré par `diff.py` sur ce chantier. Corrigé en le faisant fondre vers `opacity: 0`, sur la
  // MÊME durée que le rétrécissement du calque : les deux se terminent ensemble, la dernière
  // image du calque est alors identique au pixel près à la vraie scène, teinte comprise. Vaut
  // desktop ET mobile (le voile n'a jamais dépendu de `mobile`, ce défaut non plus).
  const styleSortieVoile = glissement
    ? { transition: `opacity ${reducedMotion ? DUREE_SORTIE_REDUITE_MS : DUREE_GLISSE_CHAMBRE_MS}ms ease-out`, opacity: 0 }
    : undefined;

  // ⟦C27 quater · défaut n°1⟧ Mobile ET glissement ET mouvement normal seulement (desktop :
  // `object-fit: cover` CSS ordinaire, inchangé ; mouvement réduit : le calque entier fond en
  // opacité, `styleSortieChambre` ci-dessus, aucun recadrage à interpoler). `transformCadrageAccueil`
  // ne dépend d'aucune mesure DOM (seulement de la fenêtre) : calculable au rendu, sans attendre
  // l'effet — c'est le repli tant que `transformImageArrivee` n'a pas encore été mesuré (même
  // patron que `transformSortie ?? undefined` juste au-dessus, à la différence près que celui-ci
  // a TOUJOURS une valeur pixel-identique au CSS d'avant, jamais un `undefined` qui ferait sauter
  // au cadrage IDENTITÉ le temps d'un rendu).
  const styleImageSortie =
    glissement && mobile && !reducedMotion
      ? {
          transformOrigin: "0 0",
          width: `${LARGEUR_IMAGE_CHAMBRE}px`,
          height: `${HAUTEUR_IMAGE_CHAMBRE}px`,
          objectFit: "fill" as const,
          transition: `transform ${DUREE_GLISSE_CHAMBRE_MS}ms ease-out`,
          transform: transformImageArrivee ?? transformCadrageAccueil(window.innerWidth, window.innerHeight),
        }
      : undefined;

  return (
    <div
      className="accueil-overlay"
      ref={racineRef}
      role="presentation"
      style={{ pointerEvents: glissement ? "none" : "auto", ...styleSortieOverlay }}
    >
      <div className="accueil-chambre-capa" ref={capaRef} style={styleSortieChambre}>
        <img className="accueil-chambre-fondo pixel-asset" src={chambrePlanche} alt="" style={styleImageSortie} />
        <div className="accueil-chambre-voile" aria-hidden="true" style={styleSortieVoile} />
      </div>

      {dureesVoix !== undefined ? (
        <div className="accueil-dialogue" style={styleSortieBoite}>
          <BoiteDialogue
            hote={HOTE_MATT}
            texte={REPLIQUES_BRIEFING[index] ?? REPLIQUES_BRIEFING[REPLIQUES_BRIEFING.length - 1]!}
            onSuite={avancer}
            actionRuban={{ label: "Passer", onClick: passer }}
            // ⟦C42 · A-165 §1⟧ Le fichier de LA réplique `index`, et rien d'autre (voir le
            // commentaire de `SOURCES_VOIX_ACCUEIL` et de `BoiteDialogueAudio`) ; `?? le dernier`
            // même repli que `texte` juste au-dessus, au cas où `index` dépasserait la longueur
            // (ne devrait jamais arriver, `avancer` ne dépasse jamais `REPLIQUES_BRIEFING.length
            // - 1`, mais aucune des deux listes ne doit planter si jamais elles divergent).
            audio={{
              src: SOURCES_VOIX_ACCUEIL[index] ?? SOURCES_VOIX_ACCUEIL[SOURCES_VOIX_ACCUEIL.length - 1],
              duree: dureesVoix[index] ?? null,
            }}
            // ⟦C29 · lot D · 7a⟧ « Passer », un tap long ou Échap sautent la conversation SANS
            // passer par le clic normal de la boîte (`BoiteDialogue.auGeste`) : sans ce signal,
            // la réplique en cours continuerait de jouer pendant toute l'animation de sortie.
            interrompreAudio={glissement}
          />
        </div>
      ) : null}
    </div>
  );
}
